import { View } from "react-native";
import { useState, useEffect } from "react";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui";
import { Text } from "../ui/text";
import { CustomCheckbox } from "../ui/custom-checkbox";
import { useRouter } from "expo-router";

interface ChecklistFormProps {
  crewId: number;
  jobDateId: number;
  crewMemberName?: string;
}

export default function ChecklistForm({ crewId, jobDateId, crewMemberName }: ChecklistFormProps) {
  const router = useRouter();
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());

  const { data: checklistData, isLoading } = trpc.probationChecklist.list.useQuery({
    crew_id: crewId,
    job_date_id: jobDateId,
    show_completed: false, // Only show incomplete items
  });

  useEffect(() => {
    if (checklistData?.data) {
      const alreadyChecked = new Set<number>();
      checklistData.data.forEach((item) => {
        // If this item has any checks for this specific job, mark it as checked
        const hasCheckForThisJob = item.crew_probation_checklists?.some(
          (check) => check.job_date_id === jobDateId
        );
        if (hasCheckForThisJob) {
          alreadyChecked.add(item.id);
        }
      });
      setCheckedItems(alreadyChecked);
    }
  }, [checklistData, jobDateId]);

  const { mutate: createChecklistItem } = trpc.probationChecklist.create.useMutation({
    onSuccess() {
      Toast.show({
        type: 'success',
        text1: 'Checklist updated successfully!'
      });
      router.back();
    },
    onError(error) {
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: error.message
      });
    }
  });

  const handleToggle = (itemId: number) => {
    const newCheckedItems = new Set(checkedItems);
    if (newCheckedItems.has(itemId)) {
      newCheckedItems.delete(itemId);
    } else {
      newCheckedItems.add(itemId);
    }
    setCheckedItems(newCheckedItems);
  };

  const handleSubmit = () => {
    if (checkedItems.size === 0) {
      Toast.show({
        type: 'info',
        text1: 'No items selected',
        text2: 'Please select at least one item to check off'
      });
      return;
    }

    // Create a checklist item for each checked item
    checkedItems.forEach((itemId) => {
      createChecklistItem({
        crew_id: crewId,
        probation_checklist_id: itemId,
        job_date_id: jobDateId,
      });
    });
  };

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-foreground">Loading checklist...</Text>
      </View>
    );
  }

  if (!checklistData || checklistData.data.length === 0) {
    return (
      <View className="flex-1 items-center justify-center p-4">
        <Text className="text-foreground text-center">
          Congratulations! You've completed all probation items, or there are no items to complete for this job.
        </Text>
      </View>
    );
  }

  // Group items by their group
  const groupedItems = checklistData.data.reduce((acc, item) => {
    const groupName = item.probation_checklist_group?.name || 'Other';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(item);
    return acc;
  }, {} as Record<string, typeof checklistData.data>);

  return (
    <View className="flex-1 gap-4">
      {Object.entries(groupedItems).map(([groupName, items]) => (
        <Card key={groupName} className="mb-4">
          <CardHeader>
            <CardTitle>{groupName}</CardTitle>
            <CardDescription>
              Check off items you've completed during this job
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            {items.map((item) => {
              const completedCount = item.crew_probation_checklists?.length || 0;
              const totalRequired = item.number_of_values;
              const isChecked = checkedItems.has(item.id);

              return (
                <View
                  key={item.id}
                  className="flex-row items-center justify-between py-3 border-b border-border"
                >
                  <View className="flex-1 pr-4">
                    <Text className="text-foreground font-medium mb-1">
                      {item.name}
                    </Text>
                    <Text className="text-muted-foreground text-sm">
                      Progress: {completedCount} / {totalRequired} completed
                    </Text>
                  </View>
                  <CustomCheckbox
                    checked={isChecked}
                    onCheckedChange={() => handleToggle(item.id)}
                    className="h-6 w-6"
                  />
                </View>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <Button
        className="mt-2 mb-8"
        size="default"
        variant="crew"
        onPress={handleSubmit}
        disabled={checkedItems.size === 0}
      >
        {`Submit checklist (${checkedItems.size} items)`}
      </Button>
    </View>
  );
}

